import { useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { sessionDisplayTitle } from './chatHistoryTypes';

type ChatSessionActionsProps = {
  sessionId: string | null;
  sessionTitle: string | null;
  messageCount: number;
  renaming: boolean;
  exporting: boolean;
  onRename: (title: string) => Promise<void>;
  onExport: (format: 'md' | 'txt') => Promise<void>;
};

export const ChatSessionActions = ({
  sessionId,
  sessionTitle,
  messageCount,
  renaming,
  exporting,
  onRename,
  onExport,
}: ChatSessionActionsProps) => {
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');

  if (!sessionId) return null;

  const displayTitle = sessionDisplayTitle({
    id: sessionId,
    title: sessionTitle,
    routeContext: null,
    createdAt: '',
    updatedAt: '',
    messageCount,
  });

  const startEditing = () => {
    setDraftTitle(sessionTitle?.trim() || displayTitle);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraftTitle('');
  };

  const saveTitle = async () => {
    const nextTitle = draftTitle.trim();
    if (!nextTitle) return;
    await onRename(nextTitle);
    setEditing(false);
  };

  return (
    <div className='flex flex-wrap items-center gap-2 border-b border-border px-4 py-2'>
      {editing ? (
        <div className='flex min-w-0 flex-1 items-center gap-2'>
          <input
            type='text'
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            maxLength={120}
            className='min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs'
            aria-label='Nuevo título de conversación'
          />
          <button
            type='button'
            onClick={() => void saveTitle()}
            disabled={renaming || !draftTitle.trim()}
            className='rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50'
          >
            {renaming ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type='button'
            onClick={cancelEditing}
            className='rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent'
          >
            Cancelar
          </button>
        </div>
      ) : (
        <>
          <p className='min-w-0 flex-1 truncate text-xs font-medium text-foreground' title={displayTitle}>
            {displayTitle}
          </p>
          <button
            type='button'
            onClick={startEditing}
            className='rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground'
            aria-label='Renombrar conversación'
          >
            <PencilIcon className='h-4 w-4' />
          </button>
          <button
            type='button'
            onClick={() => void onExport('md')}
            disabled={exporting || messageCount === 0}
            className='rounded px-2 py-1 text-[10px] text-muted-foreground hover:bg-accent disabled:opacity-50'
            title='Exportar Markdown'
          >
            .md
          </button>
          <button
            type='button'
            onClick={() => void onExport('txt')}
            disabled={exporting || messageCount === 0}
            className='rounded px-2 py-1 text-[10px] text-muted-foreground hover:bg-accent disabled:opacity-50'
            title='Exportar texto plano'
          >
            .txt
          </button>
        </>
      )}
    </div>
  );
};
